// ADR023 (BL-700) — admin-web bridge that maps a Drizzle `auctions`-row subset
// (the 5 decision stamps + 2 BL-639 denorm aggregates) to the canonical
// `AuctionPhaseKind` via the shared `deriveAuctionPhaseKind` IsomorphicService.
//
// One helper, many call sites: every migrated reader repo method returns these
// fields under these exact names, then its service/route derives the phase kind
// here. Keeps the field-mapping in one place instead of repeated at ~25 sites.
//
// BL-1444 deleted this file's other half — the legacy 6-value `auction_status`
// projection — with the catalog app that was its only consumer, and BL-1584
// discharged the naming residue that deletion left behind:
// the file, its row type, and the two column helpers in `auction-repository.ts`
// were all still named for that deleted function. Nothing here says "status
// label" any more, and the grep that once found a live row type under that name
// now finds nothing (ADR 044 — Must Consolidate).

import {
  deriveAuctionPhaseKind,
  isAuctionAwaitingClose,
  type AuctionPhaseKind,
} from '@auctogon/core/isomorphic/lifecycle';

/**
 * The `auctions`-row fields required to derive the canonical ADR-023 phase kind
 * (BL-1285): the six decision stamps, plus the two BL-639 denorm lot aggregates
 * that {@link isAuctionAwaitingCloseFromRow} reads. Timestamp columns are
 * Drizzle `mode: 'string'` → ISO strings (or null).
 *
 * BL-1516: the phase ladder itself no longer reads the two denorm aggregates —
 * the `post-sale` rung was their only consumer. They stay on this row because
 * `auctionPhaseKindColumns` already selects them everywhere and the
 * awaiting-close predicate that replaced the rung needs them.
 *
 * NOTE `pausedAt` and `preBiddingAt` are this shape's silent failure mode — both
 * are nullable, so a repository projection that forgets to SELECT
 * `auctions.paused_at` derives "never paused", and one that forgets
 * `auctions.pre_bidding_at` derives "not pre-bidding", in each case with NO type
 * error. Under the tightened two-stamp predicate the latter darkens every one of
 * the auction's lots. Every reader repo method that feeds a phase-kind
 * derivation must carry both — which is what `auctionPhaseKindColumns` in
 * `auction-repository.ts` exists to guarantee. Never hand-roll the select.
 *
 * (BL-1584 collapsed this to ONE row type. It used to extend a narrower base
 * interface that existed solely to give the legacy label its own shape; with that
 * consumer deleted, the base had no reason to exist.)
 */
export interface AuctionPhaseKindRow {
  readiedAt: string | null;
  openedAt: string | null;
  closedAt: string | null;
  deletedAt: string | null;
  /** `auctions.paused_at` — drives the `paused` rung. */
  pausedAt: string | null;
  /** `auctions.pre_bidding_at` (BL-1291) — drives the `accepting-pre-bids` rung. */
  preBiddingAt: string | null;
  /** `auctions.lots_open_count_denormalized` — 0 ⇔ all lots terminal. */
  lotsOpenCount: number;
  /** `auctions.lots_max_close_time_denormalized` — MAX effective lot close, or null. */
  lotsMaxCloseTime: string | null;
}

/**
 * Derive the canonical ADR-023 {@link AuctionPhaseKind}
 * (`draft|accepting-pre-bids|ready|paused|active|closed|deleted`) from an
 * auction row's decision stamps. BL-1285: this is what every ADMIN surface reads.
 *
 * @param row The auction row (see {@link AuctionPhaseKindRow}).
 * @returns The {@link AuctionPhaseKind}.
 * @sideEffect none — pure.
 */
export function deriveAuctionPhaseKindFromRow(row: AuctionPhaseKindRow): AuctionPhaseKind {
  return deriveAuctionPhaseKind({
    deletedAt: row.deletedAt,
    closedAt: row.closedAt,
    readiedAt: row.readiedAt,
    openedAt: row.openedAt,
    pausedAt: row.pausedAt,
    preBiddingAt: row.preBiddingAt,
  });
}

/**
 * BL-1516 — the row form of `isAuctionAwaitingClose`: has this auction's sale
 * FINISHED (every lot terminal, past the last close) without `closed_at` being
 * stamped yet?
 *
 * The question the retired `post-sale` phase used to answer. Admin callers that
 * genuinely need it — the reopen/reverse-sale landing gate's BUG-1506 re-run of
 * the go-live blockers — ask for it by name rather than by reading a phase.
 *
 * @param row The auction row (see {@link AuctionPhaseKindRow}).
 * @param now Clock for the past-close test. Defaults to `new Date()`.
 * @returns true when the sale is over and `closed_at` is not stamped.
 * @sideEffect none — pure.
 */
export function isAuctionAwaitingCloseFromRow(
  row: AuctionPhaseKindRow,
  now: Date = new Date(),
): boolean {
  return isAuctionAwaitingClose({
    phase: deriveAuctionPhaseKindFromRow(row),
    lotsOpenCount: row.lotsOpenCount,
    lotsMaxCloseTime: row.lotsMaxCloseTime,
    now,
  });
}
